import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import classNames from "classnames";
import PropTypes from "prop-types";
import { getPaginatedExpensesByParentIdAndParentType } from "~/store/selectors/selector.expenses";
import { dateToMySQLFormat } from "~/utils/date";
import { ExpenseListItemContainer } from "./ExpenseListItemContainer";
import { Group } from "./Group";
import { LimitedAccess } from "./LimitedAccess";
import SectionLoader from "../SectionLoader/SectionLoader";
import { useExpenseModalContext } from "../Tracking/context/ExpenseModalContext";

const defaultReturn = {};

export const ListGroupByDate = ({ projectId, parentType, parentId, className, withForm }) => {
  const { showLimitedAccess = false, cursorDate, loadingNext = false } = useExpenseModalContext();

  const expenses = useSelector((state) =>
    getPaginatedExpensesByParentIdAndParentType(state, parentType, parentId, cursorDate)
  );

  const itemsCollection = useMemo(() => {
    if (expenses) {
      const result = {};

      expenses.forEach((expense) => {
        const dayKey = dateToMySQLFormat(expense.record_date);

        if (!result[dayKey]) {
          result[dayKey] = {
            records: 0,
            total: 0,
            items: [],
          };
        }

        const item = { ...expense };

        result[dayKey].total += expense.value;

        result[dayKey].records += 1;
        result[dayKey].items.push(item);
      });

      return result;
    }

    return defaultReturn;
  }, [expenses]);

  return (
    <>
      {expenses.length ? (
        <div className={className}>
          {Object.entries(itemsCollection).map(([key, value]) => {
            return (
              <Group
                date={key}
                key={key}
                total={value.total}
                projectId={projectId}
                className="tw-mb-6 last-of-type:tw-mb-0"
              >
                {value.items.map((item, index) => {
                  return (
                    <ExpenseListItemContainer
                      key={`${item.id}-${index}`}
                      id={item.id}
                      className="tw-mb-1 even:tw-bg-theme-200"
                    />
                  );
                })}
              </Group>
            );
          })}
          {loadingNext && <SectionLoader className="tw-flex tw-flex-1 tw-h-auto tw-py-1" />}
        </div>
      ) : null}

      <LimitedAccess
        showLimitedAccess={showLimitedAccess}
        projectId={projectId}
        parentId={parentId}
        parentType={parentType}
        className={classNames("tw-mt-6", {
          "tw-border-t-0 tw-mt-0": !expenses.length && !withForm,
          "tw-hidden sm:tw-block": expenses.length && withForm,
        })}
      />
    </>
  );
};

ListGroupByDate.propTypes = {
  projectId: PropTypes.number.isRequired,
  parentType: PropTypes.string.isRequired,
  parentId: PropTypes.number.isRequired,
  cursorDate: PropTypes.number,
  showLimitedAccess: PropTypes.bool,
  className: PropTypes.string,
  withForm: PropTypes.bool,
};
